import { assertNever } from "@redotech/util/type";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "./dropoff-provider";
import { BlueYonderQrCodeMetadataSchema } from "./dropoff-types";

interface DropoffQrCodeEntry {
  provider?: DropoffProviderType | string | null;
  shipmentGroupId?: string | null;
  qrCode?: string | null;
  qrCodeUrl?: string | null;
  trackingNumber?: string | null;
  providerMetadata?: unknown;
  dropoffFlatRateFee?: number | null;
}

interface ReturnWithDropoffs {
  dropoffs?: {
    chosen?: boolean | null;
    qrCodes: DropoffQrCodeEntry[] | null;
  } | null;
}

export function isDropoffChosen(return_: ReturnWithDropoffs): boolean {
  return !!return_.dropoffs?.chosen;
}

export function getPendingDropoffQrCodes<
  T extends { qrCodeUrl?: string | null },
>(return_: {
  dropoffs?: { chosen?: boolean | null; qrCodes: T[] | null } | null;
}): T[] {
  return (return_.dropoffs?.qrCodes ?? []).filter((entry) => !entry.qrCodeUrl);
}

export function hasPendingDropoffQrCodes(return_: ReturnWithDropoffs): boolean {
  return (
    !!return_.dropoffs?.chosen &&
    (return_.dropoffs.qrCodes ?? []).some((entry) => !entry.qrCodeUrl)
  );
}

export function getDropoffQrCodeUris(return_: ReturnWithDropoffs): string[] {
  if (!isDropoffChosen(return_)) {
    return [];
  }
  return (return_.dropoffs?.qrCodes ?? [])
    .map((qr) => qr.qrCodeUrl)
    .filter((url): url is string => !!url);
}

export function hasCompletedDropoffQrCodes(
  return_: ReturnWithDropoffs,
): boolean {
  const qrCodes = return_.dropoffs?.qrCodes ?? [];
  return (
    !!return_.dropoffs?.chosen &&
    qrCodes.length > 0 &&
    qrCodes.every((entry) => !!entry.qrCodeUrl)
  );
}

/**
 * Finds a dropoff QR code entry by provider and optionally shipmentGroupId.
 */
export function getDropoffQrCodeByProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): DropoffQrCodeEntry | undefined {
  return (return_.dropoffs?.qrCodes ?? []).find(
    (qr) =>
      qr.provider === provider &&
      (!shipmentGroupId || qr.shipmentGroupId === shipmentGroupId),
  );
}

/**
 * Checks if a dropoff QR code exists for the given provider (completed with a URL).
 */
export function hasDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
): boolean {
  return !!getDropoffQrCodeByProvider(return_, provider)?.qrCodeUrl;
}

/**
 * Gets the QR code URL for a specific provider, optionally filtered by shipmentGroupId.
 */
export function getDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  return (
    getDropoffQrCodeByProvider(return_, provider, shipmentGroupId)?.qrCodeUrl ??
    undefined
  );
}

/**
 * Gets the provider-specific identifier for a dropoff entry.
 * - VIRTUAL: trackingNumber from the qrCode entry
 * - BLUE_YONDER: returnAuthorizationNumber from providerMetadata
 * - RETURN_BEAR: undefined
 */
export function getDropoffIdentifierForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  const entry = getDropoffQrCodeByProvider(return_, provider, shipmentGroupId);
  if (!entry) {
    return undefined;
  }

  switch (provider) {
    case VIRTUAL_PROVIDER:
      return entry.trackingNumber ?? undefined;
    case BLUE_YONDER_PROVIDER: {
      const parsed = BlueYonderQrCodeMetadataSchema.safeParse(
        entry.providerMetadata,
      );
      return parsed.success ? parsed.data.returnAuthorizationNumber : undefined;
    }
    case RETURN_BEAR_PROVIDER:
      return undefined;
    default:
      assertNever(provider);
  }
}

/**
 * Checks if a dropoff entry has a provider-specific identifier.
 */
export function hasDropoffIdentifierForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
): boolean {
  return !!getDropoffIdentifierForProvider(return_, provider);
}
